import { useState } from 'react';
import { formatDistanceToNow, parseISO, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Phone, Mail, Users, MessageSquare, Linkedin, MoreHorizontal, Plus } from 'lucide-react';
import { useInteractions, useCreateInteraction } from '@/hooks/useInteractions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { InteractionType } from '@/types';

const INTERACTION_ICONS: Record<InteractionType, React.ElementType> = {
  ligacao: Phone,
  email: Mail,
  reuniao: Users,
  whatsapp: MessageSquare,
  linkedin: Linkedin,
  outro: MoreHorizontal,
};

const INTERACTION_LABELS: Record<InteractionType, string> = {
  ligacao: 'Ligacao',
  email: 'Email',
  reuniao: 'Reuniao',
  whatsapp: 'WhatsApp',
  linkedin: 'LinkedIn',
  outro: 'Outro',
};

interface Props {
  candidateId: string;
  currentUser: string;
}

export function InteractionTimeline({ candidateId, currentUser }: Props) {
  const [open, setOpen] = useState(false);
  const [type, setType] = useState<InteractionType>('ligacao');
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [description, setDescription] = useState('');

  const { data: interactions, isLoading } = useInteractions(candidateId);
  const createInteraction = useCreateInteraction();

  function handleSubmit() {
    if (!description.trim()) return;
    createInteraction.mutate(
      {
        candidate_id: candidateId,
        type,
        interaction_date: date,
        description: description.trim(),
        responsible_name: currentUser,
      },
      {
        onSuccess: () => {
          setOpen(false);
          setDescription('');
          setDate(format(new Date(), 'yyyy-MM-dd'));
          setType('ligacao');
        },
      },
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button size="sm" variant="outline" onClick={() => setOpen((o) => !o)}>
          <Plus className="h-3.5 w-3.5" />
          Nova interacao
        </Button>
      </div>

      {open && (
        <div className="rounded-lg border border-border bg-bg-base p-3 flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-text-muted mb-1 block">Tipo</label>
              <Select value={type} onValueChange={(v) => setType(v as InteractionType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(INTERACTION_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-text-muted mb-1 block">Data</label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-text-muted mb-1 block">Descricao</label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descreva a interacao..."
              rows={2}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={!description.trim() || createInteraction.isPending}
            >
              {createInteraction.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </div>
        </div>
      )}

      {isLoading && <p className="text-xs text-text-muted">Carregando...</p>}

      <div className="flex flex-col gap-1">
        {interactions?.map((interaction) => {
          const Icon = INTERACTION_ICONS[interaction.type];
          return (
            <div key={interaction.id} className="flex gap-3 py-2">
              <div className="flex flex-col items-center">
                <div className="flex h-7 w-7 items-center justify-center rounded-full border border-border bg-bg-elevated">
                  <Icon className="h-3.5 w-3.5 text-text-secondary" />
                </div>
                <div className="w-px flex-1 bg-border mt-1" />
              </div>
              <div className="pb-3 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-medium text-text-primary">
                    {INTERACTION_LABELS[interaction.type]}
                  </span>
                  <span className="text-xs text-text-muted">
                    {interaction.interaction_date
                      ? formatDistanceToNow(parseISO(interaction.interaction_date), {
                          addSuffix: true,
                          locale: ptBR,
                        })
                      : '—'}
                  </span>
                  <span className="text-xs text-text-muted">
                    por {interaction.responsible_name}
                  </span>
                </div>
                <p className="mt-0.5 text-sm text-text-secondary">
                  {interaction.description}
                </p>
              </div>
            </div>
          );
        })}
        {interactions?.length === 0 && (
          <p className="text-xs text-text-muted text-center py-4">
            Nenhuma interacao registrada.
          </p>
        )}
      </div>
    </div>
  );
}
